import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from, of } from 'rxjs';
import { catchError, map, switchMap, tap, timeout } from 'rxjs/operators';

export interface LocationResult {
  state: string;
  district: string;
  city: string;
  cities: string[];
  pincode?: string;
}

export interface PlaceSuggestion {
  name: string;
  placeId: string;
  state: string;
  district: string;
  pincode?: string;
}

declare var google: any;

/**
 * Wraps Google Maps JavaScript API (Places + Geocoding)
 * for looking up states, districts, cities and pincodes dynamically.
 */
@Injectable({
  providedIn: 'root',
})
export class GoogleMapsLocationService {
  private http = inject(HttpClient);
  private geocoderPromise: Promise<any> | null = null;
  private placesPromise: Promise<any> | null = null;
  private autocompleteServiceInstance: any = null;
  private geocoderInstance: any = null;

  private pincodeCache = new Map<string, LocationResult>();
  private cityLocationCache = new Map<string, { city: string; district: string; state: string; pincode: string }>();
  private statesCache: string[] | null = null;
  private districtsCache = new Map<string, PlaceSuggestion[]>();

  private async getGeocoder(): Promise<any> {
    if (this.geocoderInstance) return this.geocoderInstance;
    if (!this.geocoderPromise) {
      if (typeof google === 'undefined' || !google.maps) {
        throw new Error('Google Maps SDK not loaded');
      }
      this.geocoderPromise = google.maps.importLibrary('geocoding').then((lib: any) => {
        this.geocoderInstance = new lib.Geocoder();
        return this.geocoderInstance;
      });
    }
    return this.geocoderPromise;
  }

  private placesLib: any = null;

  private async getPlacesLibrary(): Promise<any> {
    if (this.placesLib) return this.placesLib;
    if (!this.placesPromise) {
      if (typeof google === 'undefined' || !google.maps) {
        throw new Error('Google Maps SDK not loaded');
      }
      this.placesPromise = google.maps.importLibrary('places').then((lib: any) => {
        this.placesLib = lib;
        return lib;
      });
    }
    return this.placesPromise;
  }

  /**
   * Reverse-geocode a 6-digit PIN code to state/district/city using Postal PIN Code API
   * with fallback to Google Maps Geocoding API
   */
  lookupPincode(pincode: string): Observable<LocationResult | null> {
    const clean = (pincode || '').trim();
    if (!/^\d{6}$/.test(clean)) return of(null);

    if (this.pincodeCache.has(clean)) {
      return of(this.pincodeCache.get(clean)!);
    }

    return this.lookupPincodeFromPostalApi(clean).pipe(
      switchMap((result) => {
        if (result && result.state && (result.district || result.city)) {
          this.pincodeCache.set(clean, result);
          return of(result);
        }
        return this.lookupPincodeFromGoogle(clean);
      }),
      catchError(() => this.lookupPincodeFromGoogle(clean)),
      tap((finalResult) => {
        if (finalResult) {
          this.pincodeCache.set(clean, finalResult);
        }
      })
    );
  }

  private lookupPincodeFromPostalApi(clean: string): Observable<LocationResult | null> {
    const url = `https://api.postalpincode.in/pincode/${clean}`;
    return this.http.get<any[]>(url).pipe(
      timeout(3500),
      map((res) => {
        if (
          Array.isArray(res) &&
          res.length > 0 &&
          res[0]?.Status === 'Success' &&
          Array.isArray(res[0]?.PostOffice) &&
          res[0].PostOffice.length > 0
        ) {
          const postOffices = res[0].PostOffice;
          const first = postOffices[0];
          const cities = Array.from(
            new Set(postOffices.map((po: any) => (po.Name || '').trim()).filter(Boolean))
          ).sort() as string[];

          const state = (first.State || '').trim();
          const district = (first.District || '').trim();
          const headPo = postOffices.find((po: any) =>
            (po.BranchType || '').toLowerCase().includes('head')
          );
          const city = (headPo ? headPo.Name : first.Name) || district || '';

          return {
            state,
            district,
            city,
            cities,
            pincode: clean,
          };
        }
        return null;
      }),
      catchError(() => of(null))
    );
  }

  private lookupPincodeFromGoogle(clean: string): Observable<LocationResult | null> {
    return from(
      this.getGeocoder().then(
        (geocoder: any) =>
          new Promise<LocationResult | null>((resolve) => {
            geocoder.geocode(
              { address: clean + ', India', componentRestrictions: { country: 'IN' } },
              (results: any[], status: string) => {
                if (status === 'OK' && results && results.length > 0) {
                  resolve(this.extractLocationFromGeocodeResult(results[0]));
                  return;
                }
                geocoder.geocode(
                  { componentRestrictions: { country: 'IN', postalCode: clean } },
                  (results2: any[], status2: string) => {
                    if (status2 === 'OK' && results2 && results2.length > 0) {
                      resolve(this.extractLocationFromGeocodeResult(results2[0]));
                      return;
                    }
                    resolve(null);
                  }
                );
              }
            );
          })
      )
    ).pipe(catchError(() => of(null)));
  }

  /**
   * Search for Indian states using Google Maps Places Autocomplete
   */
  searchStates(term: string = ''): Observable<string[]> {
    const isDefaultQuery = !term || term.trim().length === 0;
    if (isDefaultQuery && this.statesCache && this.statesCache.length > 0) {
      return of(this.statesCache);
    }

    return from(
      this.getPlacesLibrary().then(async (places: any) => {
        const query = term && term.trim().length > 0 ? term.trim() + ', India' : 'India';

        // Try New Places API AutocompleteSuggestion if available
        if (places.AutocompleteSuggestion && places.AutocompleteSuggestion.fetchAutocompleteSuggestions) {
          try {
            const resp = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
              input: query,
              includedRegionCodes: ['in'],
              includedPrimaryTypes: ['administrative_area_level_1'],
            });
            const suggestions = resp.suggestions || [];
            const states = suggestions
              .map((s: any) => s.placePrediction?.text?.toString() || s.placePrediction?.mainText?.toString())
              .filter(Boolean);
            if (states.length > 0) {
              const unique = Array.from(new Set(states)) as string[];
              if (isDefaultQuery) this.statesCache = unique;
              return unique;
            }
          } catch (e) {
            // fallback to legacy
          }
        }

        // Fallback to AutocompleteService
        const service = this.autocompleteServiceInstance || (this.autocompleteServiceInstance = new places.AutocompleteService());
        return new Promise<string[]>((resolve) => {
          service.getPlacePredictions(
            {
              input: query,
              types: ['administrative_area_level_1'],
              componentRestrictions: { country: 'in' },
            },
            (predictions: any[], status: string) => {
              if (status !== 'OK' || !predictions) {
                resolve([]);
                return;
              }
              const states = predictions
                .map((p: any) => {
                  const stateComp = p.terms?.find((t: any) => t.value !== 'India');
                  return stateComp?.value || p.structured_formatting?.main_text || p.description?.split(',')[0];
                })
                .filter(Boolean);
              const unique = Array.from(new Set(states)) as string[];
              if (isDefaultQuery && unique.length > 0) this.statesCache = unique;
              resolve(unique);
            }
          );
        });
      })
    ).pipe(catchError(() => of([])));
  }

  /**
   * Get states dynamically via Google Maps API
   */
  getStates(): Observable<string[]> {
    return this.searchStates('');
  }

  /**
   * Search for districts dynamically using Google Maps Places API
   */
  searchDistricts(term: string = '', stateFilter?: string): Observable<PlaceSuggestion[]> {
    const isDefaultQuery = !term || term.trim().length === 0;
    const cacheKey = stateFilter ? stateFilter.toLowerCase().trim() : '';
    if (isDefaultQuery && cacheKey && this.districtsCache.has(cacheKey)) {
      return of(this.districtsCache.get(cacheKey)!);
    }

    return from(
      this.getPlacesLibrary().then(async (places: any) => {
        const query = term && term.trim().length > 0
          ? term.trim() + (stateFilter ? ', ' + stateFilter : '') + ', India'
          : (stateFilter ? stateFilter + ' district, India' : 'district, India');

        if (places.AutocompleteSuggestion && places.AutocompleteSuggestion.fetchAutocompleteSuggestions) {
          try {
            const resp = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
              input: query,
              includedRegionCodes: ['in'],
              includedPrimaryTypes: ['administrative_area_level_2', 'administrative_area_level_3'],
            });
            const suggestions = resp.suggestions || [];
            if (suggestions.length > 0) {
              const result = suggestions.map((s: any) => {
                const pred = s.placePrediction;
                const name = pred?.mainText?.toString() || pred?.text?.toString() || '';
                return {
                  name,
                  placeId: pred?.placeId || '',
                  state: stateFilter || '',
                  district: name,
                };
              });
              if (isDefaultQuery && cacheKey && result.length > 0) {
                this.districtsCache.set(cacheKey, result);
              }
              return result;
            }
          } catch (e) {
            // fallback
          }
        }

        const service = this.autocompleteServiceInstance || (this.autocompleteServiceInstance = new places.AutocompleteService());
        return new Promise<PlaceSuggestion[]>((resolve) => {
          service.getPlacePredictions(
            {
              input: query,
              types: ['(regions)'],
              componentRestrictions: { country: 'in' },
            },
            (predictions: any[], status: string) => {
              if (status !== 'OK' || !predictions) {
                resolve([]);
                return;
              }
              const result: PlaceSuggestion[] = predictions.slice(0, 20).map((p: any) => {
                const terms: { value: string }[] = p.terms || [];
                const name = terms[0]?.value || p.structured_formatting?.main_text || p.description;
                const state = terms.length > 1 ? terms[terms.length - 2]?.value : '';
                return {
                  name,
                  placeId: p.place_id,
                  state: state || stateFilter || '',
                  district: name,
                };
              });
              if (isDefaultQuery && cacheKey && result.length > 0) {
                this.districtsCache.set(cacheKey, result);
              }
              resolve(result);
            }
          );
        });
      })
    ).pipe(catchError(() => of([])));
  }

  /**
   * Search for cities dynamically with Google Maps Places API
   */
  searchCities(term: string, stateFilter?: string): Observable<PlaceSuggestion[]> {
    if (!term || term.trim().length < 2) return of([]);

    return from(
      this.getPlacesLibrary().then(async (places: any) => {
        const query = term + (stateFilter ? ', ' + stateFilter : '') + ', India';

        if (places.AutocompleteSuggestion && places.AutocompleteSuggestion.fetchAutocompleteSuggestions) {
          try {
            const resp = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
              input: query,
              includedRegionCodes: ['in'],
              includedPrimaryTypes: ['locality', 'sublocality', 'postal_town'],
            });
            const suggestions = resp.suggestions || [];
            if (suggestions.length > 0) {
              return suggestions.map((s: any) => {
                const pred = s.placePrediction;
                const city = pred?.mainText?.toString() || pred?.text?.toString() || '';
                const secondary = pred?.secondaryText?.toString() || '';
                const parts = secondary.split(',').map((x: string) => x.trim()).filter(Boolean);
                if (parts.length > 0 && parts[parts.length - 1].toLowerCase() === 'india') {
                  parts.pop();
                }
                const parsedState = parts.length > 0 ? parts.pop()! : '';
                const parsedDistrict = parts.length > 0 ? parts.pop()! : '';
                const state = stateFilter || parsedState || '';
                const district = parsedDistrict || city;

                return {
                  name: city,
                  placeId: pred?.placeId || '',
                  state,
                  district,
                };
              });
            }
          } catch (e) {
            // fallback
          }
        }

        const service = this.autocompleteServiceInstance || (this.autocompleteServiceInstance = new places.AutocompleteService());
        return new Promise<PlaceSuggestion[]>((resolve) => {
          service.getPlacePredictions(
            {
              input: query,
              types: ['(cities)'],
              componentRestrictions: { country: 'in' },
            },
            (predictions: any[], status: string) => {
              if (status !== 'OK' || !predictions) {
                resolve([]);
                return;
              }
              const result: PlaceSuggestion[] = predictions.slice(0, 20).map((p: any) => {
                const terms: { value: string }[] = (p.terms || []).slice();
                const city = terms.length > 0 ? terms[0].value : (p.structured_formatting?.main_text || p.description);
                if (terms.length > 0 && terms[terms.length - 1].value.toLowerCase() === 'india') {
                  terms.pop();
                }
                const parsedState = terms.length > 1 ? terms.pop()!.value : '';
                const parsedDistrict = terms.length > 1 ? terms.pop()!.value : '';
                const state = stateFilter || parsedState || '';
                const district = parsedDistrict || city;

                return {
                  name: city,
                  placeId: p.place_id,
                  state,
                  district,
                };
              });
              resolve(result);
            }
          );
        });
      })
    ).pipe(catchError(() => of([])));
  }

  /**
   * Get location details from placeId using Google Maps Geocoder
   */
  getPlaceDetails(placeId: string): Observable<LocationResult | null> {
    return from(
      this.getGeocoder().then(
        (geocoder: any) =>
          new Promise<LocationResult | null>((resolve) => {
            geocoder.geocode({ placeId }, (results: any[], status: string) => {
              if (status !== 'OK' || !results || results.length === 0) {
                resolve(null);
                return;
              }
              resolve(this.extractLocationFromGeocodeResult(results[0]));
            });
          })
      )
    ).pipe(catchError(() => of(null)));
  }

  /**
   * Resolve district, state, and pincode for a given city dynamically using Google Maps API.
   */
  lookupCityLocation(
    cityName: string,
    placeId?: string,
    fallbackState?: string,
    fallbackDistrict?: string
  ): Observable<{ city: string; district: string; state: string; pincode: string }> {
    const cleanCity = (cityName || '').trim();
    if (!cleanCity) {
      return of({ city: '', district: '', state: '', pincode: '' });
    }

    const cacheKey = (cleanCity + '_' + (fallbackState || '')).toLowerCase();
    if (this.cityLocationCache.has(cacheKey)) {
      return of(this.cityLocationCache.get(cacheKey)!);
    }

    return from(
      this.getGeocoder().then((geocoder: any) => {
        return new Promise<{ city: string; district: string; state: string; pincode: string }>((resolve) => {
          const req = placeId
            ? { placeId }
            : {
                address: cleanCity + (fallbackState ? ', ' + fallbackState : '') + ', India',
                componentRestrictions: { country: 'IN' },
              };

          geocoder.geocode(req, (results: any[], status: string) => {
            if (status !== 'OK' || !results || results.length === 0) {
              const fallback = {
                city: cleanCity,
                district: fallbackDistrict || cleanCity,
                state: fallbackState || '',
                pincode: '',
              };
              this.cityLocationCache.set(cacheKey, fallback);
              resolve(fallback);
              return;
            }

            const primary = this.extractLocationFromGeocodeResult(results[0]);
            let state = fallbackState || primary.state || '';
            let district = (fallbackDistrict && !fallbackDistrict.toLowerCase().includes('division'))
              ? fallbackDistrict
              : (primary.district || fallbackDistrict || cleanCity);
            let pincode = primary.pincode || '';

            // Check if any other geocode result in the list has a postal code
            if (!pincode) {
              for (const r of results) {
                const pinComp = (r.address_components || []).find((c: any) => c.types.includes('postal_code'));
                if (pinComp && pinComp.long_name) {
                  pincode = pinComp.long_name;
                  break;
                }
              }
            }

            // If still no pincode, reverse geocode the lat/lng coordinates via Google Geocoder
            if (!pincode && results[0].geometry && results[0].geometry.location) {
              geocoder.geocode({ location: results[0].geometry.location }, (revResults: any[], revStatus: string) => {
                if (revStatus === 'OK' && revResults && revResults.length > 0) {
                  for (const r of revResults) {
                    const pinComp = (r.address_components || []).find((c: any) => c.types.includes('postal_code'));
                    if (pinComp && pinComp.long_name) {
                      pincode = pinComp.long_name;
                      break;
                    }
                  }
                }
                const finalResult = { city: cleanCity, district: district || cleanCity, state, pincode };
                this.cityLocationCache.set(cacheKey, finalResult);
                resolve(finalResult);
              });
              return;
            }

            const finalResult = { city: cleanCity, district: district || cleanCity, state, pincode };
            this.cityLocationCache.set(cacheKey, finalResult);
            resolve(finalResult);
          });
        });
      })
    ).pipe(
      catchError(() => {
        const fallback = {
          city: cleanCity,
          district: fallbackDistrict || cleanCity,
          state: fallbackState || '',
          pincode: '',
        };
        this.cityLocationCache.set(cacheKey, fallback);
        return of(fallback);
      })
    );
  }

  private extractLocationFromGeocodeResult(result: any): LocationResult {
    const components: any[] = result.address_components || [];

    const get = (...types: string[]): string => {
      for (const type of types) {
        const comp = components.find((c: any) => c.types.includes(type));
        if (comp) return comp.long_name;
      }
      return '';
    };

    const state = get('administrative_area_level_1');
    const level2 = get('administrative_area_level_2');
    const level3 = get('administrative_area_level_3');
    const locality = get('locality', 'postal_town');
    const sublocality = get('sublocality_level_1');

    // In India, administrative_area_level_2 is often a Commissioner's division (e.g. "South Chotanagpur Division").
    // The actual district is found in administrative_area_level_3 or locality.
    let district = '';
    if (level2 && !level2.toLowerCase().includes('division')) {
      district = level2;
    } else if (level3) {
      district = level3;
    } else if (locality && locality.toLowerCase() !== (sublocality || '').toLowerCase()) {
      district = locality;
    } else if (level2) {
      district = level2.replace(/\s+Division$/i, '');
    }

    const city = locality || sublocality || level3 || district;
    const pincode = get('postal_code');

    const cities: string[] = [];
    if (city) cities.push(city);
    if (result.postcode_localities && Array.isArray(result.postcode_localities)) {
      cities.push(...result.postcode_localities);
    }

    return { state, district: district || city, city, cities: Array.from(new Set(cities.filter(Boolean))), pincode };
  }
}
